import { Injectable, inject } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { CookieOptions, CookieService } from './cookie.service';

/**
 * Unified storage service that automatically uses:
 * - Capacitor Preferences for native mobile apps (iOS/Android)
 * - Cookies for web applications
 * 
 * This provides a consistent API regardless of the platform.
 */
@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private cookieService = inject(CookieService);

  private isNative: boolean;

  constructor() {
    // Detect if running on a native platform (iOS or Android)
    this.isNative = Capacitor.isNativePlatform();
  }

  /**
   * Store a value
   * @param key - The key to store the value under
   * @param value - The value to store
   * @param persist - For web only: whether to persist the cookie (adds expiration)
   * @param options - For web only: additional cookie options
   */
  async set(
    key: string,
    value: string,
    persist = false,
    options?: CookieOptions
  ): Promise<void> {
    if (this.isNative) {
      // Use Capacitor Preferences for native apps
      await Preferences.set({ key, value });
    } else {
      // Use cookies for web
      if (!persist) {
        this.cookieService.setCookie(key, value, { path: '/', ...options });
      } else {
        this.cookieService.setCookie(key, value, {
          expires: 1, // 1 day
          path: '/',
          ...options,
        });
      }
    }
  }

  /**
   * Retrieve a value
   * @param key - The key to retrieve
   * @returns The stored value or null if not found
   */
  async get(key: string): Promise<string | null> {
    if (this.isNative) {
      // Use Capacitor Preferences for native apps
      const { value } = await Preferences.get({ key });
      return value;
    } else {
      // Use cookies for web
      return this.cookieService.getCookie(key) ?? null;
    }
  }

  /**
   * Remove a value
   * @param key - The key to remove
   */
  async remove(key: string): Promise<void> {
    if (this.isNative) {
      // Use Capacitor Preferences for native apps
      await Preferences.remove({ key });
    } else {
      // Use cookies for web
      this.cookieService.deleteCookie(key, '/');
    }
  }

  /**
   * Clear all stored values
   * Note: On web, this only clears cookies managed by this service
   */
  async clear(): Promise<void> {
    if (this.isNative) {
      // Use Capacitor Preferences for native apps
      await Preferences.clear();
    } else {
      // For web, we would need to track all keys or clear specific known keys
      // This is a limitation of the cookie approach
      console.warn('Clear all is not fully supported for web cookies');
    }
  }

  /**
   * Check if running on a native platform
   * @returns true if running on iOS or Android, false for web
   */
  isNativePlatform(): boolean {
    return this.isNative;
  }
}
